import {
  ShieldCheck,
  Users,
  Settings,
  LayoutGrid,
  TrendingUp,
  UserCog,
  Wrench,
  Shield,
  UserCheck,
  ArrowLeftRight,
  Rss,
  Palette,
  ToggleLeft,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type AdminGroup = 'oversight' | 'resources' | 'system';

export type AdminTab =
  | 'dashboard'
  | 'activity'
  | 'users'
  | 'settings'
  | 'features'
  | 'roles'
  | 'user-approvals'
  | 'user-impersonation'
  | 'rss-feeds'
  | 'colors';

export interface AdminNavItem {
  tabId: AdminTab;
  path: string;
  labelKey: string;
  icon: LucideIcon;
  group: AdminGroup;
}

export interface AdminGroupConfig {
  id: AdminGroup;
  labelKey: string;
  icon: LucideIcon;
}

export const ADMIN_GROUPS: AdminGroupConfig[] = [
  { id: 'oversight', labelKey: 'adminNav.oversight', icon: ShieldCheck },
  { id: 'resources', labelKey: 'adminNav.resources', icon: Users },
  { id: 'system', labelKey: 'adminNav.system', icon: Settings },
];

export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  // Oversight
  { tabId: 'dashboard', path: '/admin', labelKey: 'adminNav.dashboard', icon: LayoutGrid, group: 'oversight' },
  { tabId: 'activity', path: '/admin/activity', labelKey: 'adminNav.activityLog', icon: TrendingUp, group: 'oversight' },
  // Resources
  { tabId: 'user-approvals', path: '/admin/user-approvals', labelKey: 'adminNav.userApprovals', icon: UserCheck, group: 'resources' },
  { tabId: 'users', path: '/admin/users', labelKey: 'adminNav.usersManagement', icon: UserCog, group: 'resources' },
  { tabId: 'roles', path: '/admin/roles', labelKey: 'adminNav.roles', icon: Shield, group: 'resources' },
  { tabId: 'user-impersonation', path: '/admin/user-impersonation', labelKey: 'adminNav.userImpersonation', icon: ArrowLeftRight, group: 'resources' },
  // System
  { tabId: 'settings', path: '/admin/settings', labelKey: 'adminNav.systemSettings', icon: Wrench, group: 'system' },
  { tabId: 'rss-feeds', path: '/admin/rss-feeds', labelKey: 'adminNav.rssFeeds', icon: Rss, group: 'system' },
  { tabId: 'colors', path: '/admin/colors', labelKey: 'adminNav.colors', icon: Palette, group: 'system' },
  { tabId: 'features', path: '/admin/features', labelKey: 'adminNav.featureFlags', icon: ToggleLeft, group: 'system' },
];

/** Permission-based access for admin tabs using DB permissions (tab:*) */
function tabToPermission(tab: AdminTab): string {
  return `tab:${tab.replace(/-/g, '_')}`;
}

const ALWAYS_VISIBLE_TABS: Set<AdminTab> = new Set(['user-approvals']);

export function canAccessAdminTab(tab: AdminTab, permissions: string[]): boolean {
  if (ALWAYS_VISIBLE_TABS.has(tab)) return true;
  return permissions.includes(tabToPermission(tab));
}

/** Map a route path to its group */
export function getGroupFromPath(pathname: string): AdminGroup | null {
  if (pathname === '/admin') return 'oversight';
  const item = ADMIN_NAV_ITEMS.find(
    i => i.path !== '/admin' && (pathname === i.path || pathname.startsWith(i.path + '/')),
  );
  return item?.group ?? null;
}
